import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { ChevronDown, ChevronRight, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIF_LABELS = {
  calls:         'Calls',
  tasks:         'CRM Tasks',
  orders_quotes: 'Orders/Quotes',
  stock:         'Stock',
  pi_counts:     'PI Counts',
};

const NOTIF_TYPES = ['calls', 'tasks', 'orders_quotes', 'stock', 'pi_counts'];

/**
 * NotificationBarSettings — super admin only tab inside DashboardManager.
 *
 * Shows all users grouped by branch. Each branch has an on/off toggle.
 * Expanding a branch shows users with 5 notification toggles + drag-drop order.
 */
export const NotificationBarSettings = () => {
  const [branches, setBranches] = useState([]);
  const [expandedBranches, setExpandedBranches] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`${API}/notification-bar/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data.branches || []);
    } catch (e) {
      console.error('Failed to load notification bar settings', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBranch = async (branchId, currentEnabled) => {
    const newVal = !currentEnabled;
    setBranches(prev => prev.map(b =>
      b.branch_id === branchId ? { ...b, enabled: newVal } : b
    ));
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/notification-bar/settings/branch`, { branch_id: branchId, enabled: newVal }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await load();
    } catch {
      setBranches(prev => prev.map(b =>
        b.branch_id === branchId ? { ...b, enabled: currentEnabled } : b
      ));
    }
  };

  const toggleUserEnabled = async (userId, currentEnabled, branchIdx, userIdx) => {
    const newVal = !currentEnabled;
    // Optimistic update
    setBranches(prev => {
      const next = [...prev];
      next[branchIdx] = { ...next[branchIdx], users: [...next[branchIdx].users] };
      const u = { ...next[branchIdx].users[userIdx] };
      u.settings = { ...u.settings, enabled: newVal };
      next[branchIdx].users[userIdx] = u;
      return next;
    });
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/notification-bar/settings/user`, { user_id: userId, enabled: newVal }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { await load(); }
  };

  const toggleNotif = async (userId, notifType, currentEnabled, branchIdx, userIdx) => {
    // Optimistic update
    setBranches(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const user = next[branchIdx].users[userIdx];
      const notifs = user.settings.notifications || NOTIF_TYPES.map((t, i) => ({ type: t, enabled: false, position: i, label: NOTIF_LABELS[t] }));
      const notif = notifs.find(n => n.type === notifType);
      if (notif) notif.enabled = !currentEnabled;
      user.settings.notifications = notifs;
      return next;
    });
    try {
      const token = localStorage.getItem('token');
      const user = branches[branchIdx].users[userIdx];
      const notifs = (user.settings.notifications || NOTIF_TYPES.map((t, i) => ({ type: t, enabled: false, position: i, label: NOTIF_LABELS[t] }))).map(n =>
        n.type === notifType ? { ...n, enabled: !currentEnabled } : n
      );
      await api.put(`${API}/notification-bar/settings/user`, { user_id: userId, notifications: notifs }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { await load(); }
  };

  const moveNotif = async (userId, fromIdx, toIdx, branchIdx, userIdx) => {
    // Compute reordered array from current state before calling setState
    const currentUser = branches[branchIdx].users[userIdx];
    const notifs = [...(currentUser.settings.notifications || NOTIF_TYPES.map((t, i) => ({ type: t, enabled: false, position: i, label: NOTIF_LABELS[t] })))].sort((a, b) => {
      const pA = typeof a.position === 'number' ? a.position : (typeof a.order === 'number' ? a.order : 999);
      const pB = typeof b.position === 'number' ? b.position : (typeof b.order === 'number' ? b.order : 999);
      return pA - pB;
    });
    const reordered = [...notifs];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    reordered.forEach((n, i) => { n.position = i; });

    // Optimistic state update using the already-computed reordered array
    setBranches(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next[branchIdx].users[userIdx].settings.notifications = reordered;
      return next;
    });

    // Save to backend using same reordered array (fixes stale closure bug)
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/notification-bar/settings/user`, { user_id: userId, enabled: true, notifications: reordered }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { await load(); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-gray-400">Loading settings…</div>
  );

  return (
    <div className="space-y-3" data-testid="notification-bar-settings">
      <div className="mb-4">
        <h3 className="font-bold text-gray-800 text-base">Notification Bar Settings</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Control which users see which dashboard notifications and in what order.
          Stock and PI Counts are always admin-only regardless of toggle.
        </p>
      </div>

      {branches.length === 0 && (
        <div className="text-center text-gray-400 py-8 text-sm">No users found.</div>
      )}

      {branches.map((branch, branchIdx) => (
        <div key={branch.branch_id} className="border rounded-xl overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
          {/* Branch header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b" style={{ borderColor: '#e5e7eb' }}>
            <button
              className="flex items-center gap-2 font-bold text-gray-800 hover:text-orange-600 transition-colors"
              onClick={() => setExpandedBranches(p => ({ ...p, [branch.branch_id]: !p[branch.branch_id] }))}
            >
              {expandedBranches[branch.branch_id]
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronRight className="w-4 h-4" />}
              <span>{branch.branch_id}</span>
              <span className="text-xs text-gray-400 font-normal">({branch.users?.length || 0} users)</span>
            </button>

            {/* Branch on/off toggle */}
            <button
              onClick={() => toggleBranch(branch.branch_id, branch.enabled)}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: branch.enabled ? '#ee7203' : '#9ca3af' }}
              title={branch.enabled ? 'Disable notifications for this branch' : 'Enable notifications for this branch'}
              data-testid={`branch-toggle-${branch.branch_id}`}
            >
              {branch.enabled
                ? <ToggleRight className="w-5 h-5" />
                : <ToggleLeft className="w-5 h-5" />}
              <span>{branch.enabled ? 'On' : 'Off'}</span>
            </button>
          </div>

          {/* Users list */}
          {expandedBranches[branch.branch_id] && (
            <div className="divide-y" style={{ borderColor: '#f3f4f6' }}>
              {(branch.users || []).map((user, userIdx) => {
                const notifs = (user.settings?.notifications || NOTIF_TYPES.map((t, i) => ({
                  type: t, enabled: false, position: i, label: NOTIF_LABELS[t],
                }))).sort((a, b) => {
                  const pA = typeof a.position === 'number' ? a.position : (typeof a.order === 'number' ? a.order : 999);
                  const pB = typeof b.position === 'number' ? b.position : (typeof b.order === 'number' ? b.order : 999);
                  return pA - pB;
                });

                return (
                  <div key={user.user_id} className="px-4 py-3 flex items-center gap-4" data-testid={`notif-user-row-${user.user_id}`}>
                    {/* User name + global toggle */}
                    <div className="flex items-center gap-2 w-40 flex-shrink-0">
                      <button
                        onClick={() => toggleUserEnabled(user.user_id, user.settings?.enabled !== false, branchIdx, userIdx)}
                        className="transition-colors"
                        style={{ color: user.settings?.enabled !== false ? '#ee7203' : '#9ca3af' }}
                        title="Enable/disable all notifications for this user"
                        data-testid={`user-notif-toggle-${user.user_id}`}
                      >
                        {user.settings?.enabled !== false
                          ? <ToggleRight className="w-5 h-5" />
                          : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <span className="text-sm font-medium text-gray-700 truncate">{user.full_name || user.user_id}</span>
                    </div>

                    {/* 5 notification toggles — drag to reorder */}
                    <div className="flex items-center gap-2 flex-1 flex-wrap">
                      {notifs.map((notif, nIdx) => (
                        <DraggableNotifToggle
                          key={notif.type}
                          notif={notif}
                          nIdx={nIdx}
                          userId={user.user_id}
                          branchIdx={branchIdx}
                          userIdx={userIdx}
                          onToggle={toggleNotif}
                          onMove={moveNotif}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};


/** Single drag-and-drop notification toggle pill */
const DraggableNotifToggle = ({ notif, nIdx, userId, branchIdx, userIdx, onToggle, onMove }) => {
  const [dragging, setDragging] = useState(false);

  return (
    <div
      draggable
      onDragStart={e => { setDragging(true); e.dataTransfer.setData('notif-idx', String(nIdx)); }}
      onDragEnd={() => setDragging(false)}
      onDragOver={e => e.preventDefault()}
      onDrop={e => {
        e.preventDefault();
        const from = parseInt(e.dataTransfer.getData('notif-idx'), 10);
        if (from !== nIdx) onMove(userId, from, nIdx, branchIdx, userIdx);
      }}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-medium cursor-grab transition-all select-none ${
        notif.enabled
          ? 'bg-orange-50 border-orange-200 text-orange-800'
          : 'bg-gray-50 border-gray-200 text-gray-400'
      } ${dragging ? 'opacity-40 scale-95' : 'hover:shadow-sm'}`}
      data-testid={`notif-toggle-${userId}-${notif.type}`}
    >
      <GripVertical className="w-3 h-3 opacity-40" />
      <button
        onClick={() => onToggle(userId, notif.type, notif.enabled, branchIdx, userIdx)}
        className="flex items-center gap-1 focus:outline-none"
        title={`${notif.enabled ? 'Disable' : 'Enable'} ${notif.label || NOTIF_LABELS[notif.type]}`}
      >
        {notif.enabled
          ? <ToggleRight className="w-3.5 h-3.5" />
          : <ToggleLeft className="w-3.5 h-3.5" />}
        <span>{notif.label || NOTIF_LABELS[notif.type] || notif.type}</span>
      </button>
    </div>
  );
};

export default NotificationBarSettings;
